import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Switch } from '@headlessui/react'
import classnames from 'classnames'

const EXPAND_ALL_BUTTON =
  'text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300'

const AUTO_SCROLL_TOOLTIP =
  'Automatically scroll an account into view when you expand it or change its tab.'

/** Portaled so sticky toolbar z-index cannot cover the tooltip. */
const ToolbarTooltip: React.FC<{ content: string; children: React.ReactNode }> = ({ content, children }) => {
  const targetRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const show = (): void => {
    const rect = targetRef.current?.getBoundingClientRect()
    if (rect == null) return
    setPos({ top: rect.bottom + 6, left: rect.left + rect.width / 2 })
    setOpen(true)
  }

  return (
    <>
      <div ref={targetRef} className="w-fit" onMouseEnter={show} onMouseLeave={() => setOpen(false)}>
        {children}
      </div>
      {open &&
        createPortal(
          <div
            role="tooltip"
            className="pointer-events-none fixed z-[100] max-w-xs -translate-x-1/2 rounded-lg border border-transparent bg-slate-700 px-3 py-2 text-left text-xs font-medium text-white shadow-sm select-none dark:border-0 dark:bg-slate-950"
            style={{ top: pos.top, left: pos.left }}
          >
            {content}
          </div>,
          document.body
        )}
    </>
  )
}

export interface TenantAccountsToolbarProps {
  accountCount: number
  allExpanded: boolean
  onToggleAll: () => void
  autoScroll: boolean
  onAutoScrollChange: (enabled: boolean) => void
  /** Distance from viewport top to stick under the page header. */
  stickyOffset?: number
  toolbarRef?: React.Ref<HTMLDivElement>
}

export const TenantAccountsToolbar: React.FC<TenantAccountsToolbarProps> = ({
  accountCount,
  allExpanded,
  onToggleAll,
  autoScroll,
  onAutoScrollChange,
  stickyOffset = 0,
  toolbarRef,
}) => {
  return (
    <div
      ref={toolbarRef}
      // Padding-bottom (not margin) so sticky page-bg fills the space down to the account header.
      className="sticky z-30 mt-8 -mx-4 bg-slate-100 px-4 pt-2.5 pb-[1.875rem] dark:bg-slate-900 sm:-mx-6 sm:px-6 md:-mx-8 md:px-8"
      style={{ top: stickyOffset }}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-lg font-normal dark:text-white">
          <span>Tenant Accounts</span>
          {accountCount > 0 && (
            <span className="inline-flex items-center justify-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-500/30 dark:text-indigo-200">
              {accountCount}
            </span>
          )}
        </div>
        {accountCount > 1 && (
          <div className="flex flex-wrap items-center gap-4">
            <ToolbarTooltip content={AUTO_SCROLL_TOOLTIP}>
              <Switch.Group as="div" className="flex items-center gap-2">
                <Switch.Label as="span" className="text-xs font-medium text-slate-600 dark:text-slate-300" passive>
                  Auto scroll
                </Switch.Label>
                <Switch
                  checked={autoScroll}
                  onChange={onAutoScrollChange}
                  className={classnames(
                    'relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2',
                    autoScroll ? 'bg-emerald-500 dark:bg-emerald-600' : 'bg-gray-200 dark:bg-gray-700'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={classnames(
                      autoScroll ? 'translate-x-4' : 'translate-x-0',
                      'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                    )}
                  />
                </Switch>
              </Switch.Group>
            </ToolbarTooltip>
            <button type="button" onClick={onToggleAll} className={EXPAND_ALL_BUTTON}>
              {allExpanded ? 'Collapse all' : 'Expand all'}
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

export default TenantAccountsToolbar
